import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Filter, NotificationStatus } from 'api/notifications';
import { Main } from 'components/atoms/main';
import { Filters } from './filters';
import { Menu } from './menu';
import { Refresh } from './refresh';
import { renderContent } from './render-content';
import { useFilters } from './use-filters';
import { useListState } from './use-list-state';

interface Props {
  pending: number;
  configScreenUrl: string;
  resetPending: () => void;
  isCustomerScreen?: boolean;
}

const Container = styled(Main)`
  padding-top: 0;
`;

const FilterStickyContainer = styled.div<{ isCustomerScreen?: boolean }>`
  position: sticky;
  top: ${(props) => (props.isCustomerScreen ? '58px' : 0)};
  z-index: var(--zindex-elevated);
`;

const TabsStickyContainer = styled.div<{ isCustomerScreen?: boolean }>`
  position: sticky;
  top: ${(props) => (props.isCustomerScreen ? '106px' : '48px')};
  z-index: 5;
`;

export const List = forwardRef(
  (
    { configScreenUrl, pending, resetPending, isCustomerScreen }: Props,
    ref,
  ) => {
    const filterProps = useFilters();
    const { status, setStatus, refresh, content } = useListState(
      filterProps.selected,
    );
    const setFilter = filterProps.onChange;
    const filter = filterProps.selected;
    const _refresh = useCallback(() => {
      if (filter !== Filter.All || status !== NotificationStatus.New) {
        setFilter(Filter.All);
        setStatus(NotificationStatus.New);
      } else {
        refresh();
        resetPending();
      }
    }, [filter, status, setFilter, setStatus, refresh, resetPending]);

    useImperativeHandle(ref, () => ({
      refresh: _refresh,
    }));

    const fetched =
      content[0] === 'fetched' || content[0] === 'fetched_last_page';

    return (
      <>
        <FilterStickyContainer isCustomerScreen={isCustomerScreen}>
          <Filters
            selected={filterProps.selected}
            options={filterProps.options}
            onChange={filterProps.onChange}
            configScreenUrl={configScreenUrl}
          />
        </FilterStickyContainer>
        <Container>
          <TabsStickyContainer isCustomerScreen={isCustomerScreen}>
            {status === NotificationStatus.New && fetched && (
              <Refresh pending={pending} refresh={_refresh} />
            )}
            <Menu
              pending={pending}
              status={status}
              setStatus={setStatus}
              refresh={_refresh}
            />
          </TabsStickyContainer>
          {renderContent(status, content)}
        </Container>
      </>
    );
  },
);
